import { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { Loader2 } from 'lucide-react';
import { formatDateTime } from '../utils';

const formatSenderName = (sender) => {
  if (!sender) return 'Unknown';
  return [sender.firstName, sender.lastName].filter(Boolean).join(' ').trim() || 'Unknown';
};

export default function MessageThreadView({
  messages = [],
  currentUserId,
  isLoading = false,
  emptyMessage = 'No messages yet. Send the first message to start the conversation.',
  showSenderNames = false,
}) {
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  if (isLoading && messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading messages…
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center px-6 text-center text-sm text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
      {messages.map((message) => {
        const isMine = message.senderId === currentUserId;
        const senderName = formatSenderName(message.sender);

        return (
          <div
            key={message.id}
            className={clsx('flex flex-col', isMine ? 'items-end' : 'items-start')}
          >
            {showSenderNames && !isMine && (
              <span className="mb-1 text-2xs font-medium text-gray-500 px-1">{senderName}</span>
            )}
            <div
              className={clsx(
                'max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
                isMine
                  ? 'bg-primary-600 text-white'
                  : 'bg-slate-100 border border-slate-200 text-gray-800'
              )}
            >
              {message.body}
            </div>
            <span className={clsx('mt-1 text-2xs px-1', isMine ? 'text-gray-400' : 'text-gray-500')}>
              {formatDateTime(message.createdAt)}
            </span>
          </div>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
}
